import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import EditIcon from '@mui/icons-material/Edit';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAppTheme } from '../App';
import { COLORS } from '../common/colors';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSubscription } from '../hooks/useSubscription';
import AnalyticsCards from '../components/AnalyticsCards';

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();
    const { analyticsData, loading } = useAnalytics();
    const { isPro } = useSubscription();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    return (
        <Box
            sx={{
                height: isMobile ? 'calc(100vh - 64px)' : 'calc(100vh - 64px)',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: theme.background,
                p: { xs: 2, sm: 3, md: 4 },
                overflow: 'auto',
                }}
            >
            {/* Main Content Layout */}
            <Box sx={{
                position: 'relative',
                maxWidth: 1400,
                mx: 'auto',
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                pt: { xs: 2, sm: 3, md: 4 }
            }}>
                {/* Left Statistics - Hidden on mobile to prevent overlap */}
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <AnalyticsCards analyticsData={analyticsData} loading={loading} position="left" />
                </Box>

                {/* Center Content */}
                <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 3, sm: 4, md: 5 },
                    maxWidth: 900,
                    mx: 'auto',
                    px: { xs: 0.5, sm: 1, lg: 2 },
                    flex: 1
                }}>
                    {/* Introductory Text */}
                        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
                            <Typography
                            variant="body1"
                                sx={{
                                    color: theme.textSecondary,
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                maxWidth: 600,
                                    mx: 'auto',
                                lineHeight: 1.5,
                                fontStyle: 'italic',
                                }}
                            >
                            Designed by successful Global Talent Visa holders, to help you succeed on your Global Talent Visa.
                            </Typography>
                        </Box>
                        
                    {/* Tools Section */}
                    <Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: { xs: 3, sm: 4 },
                            maxWidth: 900,
                            mx: 'auto'
                        }}>
                            {/* GTV Eligibility Checker */}
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(76, 175, 80, 0.02) 100%)`,
                                    boxShadow: `0 6px 24px ${theme.shadowColor}`,
                                    backdropFilter: 'blur(20px)',
                                    border: `1px solid rgba(76, 175, 80, 0.1)`,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%)`,
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: `0 16px 32px rgba(76, 175, 80, 0.15)`,
                                        background: `linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)`,
                                        border: `1px solid rgba(76, 175, 80, 0.2)`,
                                    },
                                }}
                                elevation={0}
                                onClick={() => navigate('/visa/gtv-eligibility-checker')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ position: 'relative', mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 56, sm: 64 },
                                                height: { xs: 56, sm: 64 },
                                                borderRadius: '50%',
                                                background: `linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.3)',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: -2,
                                                    left: -2,
                                                    right: -2,
                                                    bottom: -2,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, transparent 100%)`,
                                                    zIndex: -1,
                                                }
                                            }}
                                        >
                                            <AssessmentIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
                                        </Box>
                                        <Chip
                                            label={isPro ? "PRO" : "LIMITED"}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: -10,
                                                background: isPro 
                                                    ? `linear-gradient(135deg, #FE6A2E 0%, #FF8F5A 100%)` 
                                                    : `linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)`,
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                boxShadow: isPro 
                                                    ? '0 3px 8px rgba(254, 106, 46, 0.3)' 
                                                    : '0 3px 8px rgba(76, 175, 80, 0.3)',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.textPrimary,
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        }}
                                    >
                                        GTV Eligibility Checker
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textSecondary,
                                            lineHeight: 1.6,
                                            flex: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 3,
                                        }}
                                    >
                                        {isPro 
                                            ? "Unlimited comprehensive analysis of your professional profile with detailed gap assessment and success probability calculation"
                                            : "Get 3 free daily analyses of your professional profile. Upgrade to Pro for unlimited access"
                                        }
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 'auto'
                                    }}>
                                        <Box sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            background: '#4CAF50' 
                                        }} />
                                        <Typography variant="body2" sx={{ 
                                            color: theme.textSecondary,
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                        }}>
                                            {isPro ? "Unlimited Analyses" : "3 Free Daily Analyses"}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Personal Statement Generator */}
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, rgba(254, 106, 46, 0.05) 0%, rgba(254, 106, 46, 0.02) 100%)`,
                                    boxShadow: `0 6px 24px ${theme.shadowColor}`,
                                    backdropFilter: 'blur(20px)',
                                    border: `1px solid rgba(254, 106, 46, 0.1)`,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, ${COLORS.primary} 0%, #FF8F5A 100%)`,
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: `0 16px 32px rgba(254, 106, 46, 0.15)`,
                                        background: `linear-gradient(135deg, rgba(254, 106, 46, 0.08) 0%, rgba(254, 106, 46, 0.03) 100%)`,
                                        border: `1px solid rgba(254, 106, 46, 0.2)`,
                                    },
                                }}
                                elevation={0}
                                onClick={() => navigate('/visa/personal-statement')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ position: 'relative', mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 56, sm: 64 },
                                                height: { xs: 56, sm: 64 },
                                                borderRadius: '50%',
                                                background: `linear-gradient(135deg, ${COLORS.primary} 0%, #FF8F5A 100%)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                boxShadow: `0 6px 20px rgba(254, 106, 46, 0.3)`,
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: -2,
                                                    left: -2,
                                                    right: -2,
                                                    bottom: -2,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, rgba(254, 106, 46, 0.3) 0%, transparent 100%)`,
                                                    zIndex: -1,
                                                }
                                            }}
                                        >
                                            <EditIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
                                        </Box>
                                        <Chip
                                            label="PRO"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: -10,
                                                background: `linear-gradient(135deg, ${COLORS.primary} 0%, #FF8F5A 100%)`,
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                boxShadow: `0 3px 8px rgba(254, 106, 46, 0.3)`,
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.textPrimary,
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        }}
                                    >
                                        GTV Personal Statement Generator
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textSecondary,
                                            lineHeight: 1.6,
                                            flex: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 3,
                                        }}
                                    >
                                        Generate compelling personal statements tailored to your achievements and visa requirements
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 'auto'
                                    }}>
                                        <Box sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            background: COLORS.primary 
                                        }} />
                                        <Typography variant="body2" sx={{ 
                                            color: theme.textSecondary,
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                        }}>
                                            AI-Generated Content
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>

                    {/* Additional Tools Section */}
                    <Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                            gap: { xs: 3, sm: 4 },
                            maxWidth: 900,
                            mx: 'auto'
                        }}>
                            {/* Tourist Visa */}
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, rgba(156, 39, 176, 0.05) 0%, rgba(156, 39, 176, 0.02) 100%)`,
                                    boxShadow: `0 6px 24px ${theme.shadowColor}`,
                                    backdropFilter: 'blur(20px)',
                                    border: `1px solid rgba(156, 39, 176, 0.1)`,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    opacity: 0.8,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, #9C27B0 0%, #BA68C8 100%)`,
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        opacity: 1,
                                        boxShadow: `0 16px 32px rgba(156, 39, 176, 0.15)`,
                                        background: `linear-gradient(135deg, rgba(156, 39, 176, 0.08) 0%, rgba(156, 39, 176, 0.03) 100%)`,
                                        border: `1px solid rgba(156, 39, 176, 0.2)`,
                                    },
                                }}
                                elevation={0}
                                onClick={() => navigate('/visa/tourist-visa')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ position: 'relative', mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 56, sm: 64 },
                                                height: { xs: 56, sm: 64 },
                                                borderRadius: '50%',
                                                background: `linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                boxShadow: '0 6px 20px rgba(156, 39, 176, 0.3)',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: -2,
                                                    left: -2,
                                                    right: -2,
                                                    bottom: -2,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, rgba(156, 39, 176, 0.3) 0%, transparent 100%)`,
                                                    zIndex: -1,
                                                }
                                            }}
                                        >
                                            <FlightTakeoffIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
                                        </Box>
                                        <Chip
                                            label="BETA"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: -10,
                                                background: `linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)`,
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                boxShadow: '0 3px 8px rgba(156, 39, 176, 0.3)',
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.textPrimary,
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        }}
                                    >
                                        Tourist Visa Assistant
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textSecondary,
                                            lineHeight: 1.6,
                                            flex: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 3,
                                        }}
                                    >
                                        Intelligent guidance and document preparation for tourist visa applications with country-specific requirements
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 'auto'
                                    }}>
                                        <Box sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            background: '#9C27B0' 
                                        }} />
                                        <Typography variant="body2" sx={{ 
                                            color: theme.textSecondary,
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                        }}>
                                            Multi-Country Support
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Global Talent Visa Chat */}
                            <Card
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    background: `linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)`,
                                    boxShadow: `0 6px 24px ${theme.shadowColor}`,
                                    backdropFilter: 'blur(20px)',
                                    border: `1px solid rgba(59, 130, 246, 0.1)`,
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    opacity: 0.8,
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: `linear-gradient(90deg, #3B82F6 0%, #60A5FA 100%)`,
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        opacity: 1,
                                        boxShadow: `0 16px 32px rgba(59, 130, 246, 0.15)`,
                                        background: `linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)`,
                                        border: `1px solid rgba(59, 130, 246, 0.2)`,
                                    },
                                }}
                                elevation={0}
                                onClick={() => navigate('/visa/global-talent-visa')}
                            >
                                <CardContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ position: 'relative', mb: 3 }}>
                                        <Box
                                            sx={{
                                                width: { xs: 56, sm: 64 },
                                                height: { xs: 56, sm: 64 },
                                                borderRadius: '50%',
                                                background: `linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mx: 'auto',
                                                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.3)',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: -2,
                                                    left: -2,
                                                    right: -2,
                                                    bottom: -2,
                                                    borderRadius: '50%',
                                                    background: `linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, transparent 100%)`,
                                                    zIndex: -1,
                                                }
                                            }}
                                        >
                                            <WorkIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: 'white' }} />
                                        </Box>
                                        <Chip
                                            label="PRO"
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -10,
                                                right: -10,
                                                background: `linear-gradient(135deg, #FE6A2E 0%, #FF8F5A 100%)`,
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.65rem',
                                                height: 20,
                                                boxShadow: `0 3px 8px rgba(254, 106, 46, 0.3)`,
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.textPrimary,
                                            mb: 2,
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                        }}
                                    >
                                        Global Talent Visa Chat
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.textSecondary,
                                            lineHeight: 1.6,
                                            flex: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' },
                                            mb: 3,
                                        }}
                                    >
                                        {isPro 
                                            ? "Unlimited interactive AI assistant providing real-time guidance and personalized recommendations for your application"
                                            : "Interactive AI assistant with unlimited chat access. Upgrade to Pro to unlock this feature"
                                        }
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 'auto'
                                    }}>
                                        <Box sx={{ 
                                            width: 6, 
                                            height: 6, 
                                            borderRadius: '50%', 
                                            background: '#FE6A2E' 
                                        }} />
                                        <Typography variant="body2" sx={{ 
                                            color: theme.textSecondary,
                                            fontWeight: 500,
                                            fontSize: '0.8rem'
                                        }}>
                                            {isPro ? "Unlimited AI Chat" : "Pro Feature"}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                </Box>

                {/* Right Statistics */}
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <AnalyticsCards analyticsData={analyticsData} loading={loading} position="right" />
                </Box>
            </Box>
        </Box>
    );
};

export default Dashboard; 